import React, { useState, useEffect } from 'react';
import { a2aClassService } from '../../a2a/services/a2aClassService.js';
import { getTenantIdFromUser } from '../../a2a/tenantHelper.js';
import './ClassManagementModule.css';

const ClassManagementModule = ({ currentUser, onBack, onTabChange }) => {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (currentUser) {
      fetchTeacherClasses();
    }
  }, [currentUser]);

  const fetchTeacherClasses = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get classes for the teacher's tenant (multi-tenant support)
      const tenantId = getTenantIdFromUser(currentUser);
      
      // Fetch all classes and filter by teacher if needed
      const result = await a2aClassService.getClasses({
        tenantId: tenantId,
        page: 1,
        limit: 100
      });
      
      if (result.success) {
        // Filter classes for this teacher if teacher_id is available
        let teacherClasses = result.data || [];
        
        // If we have teacher_id, filter classes assigned to this teacher
        if (currentUser?.id) {
          // Note: This assumes the class data includes teacher assignments
          // You may need to adjust this based on your actual data structure
          teacherClasses = teacherClasses.filter(cls => 
            cls.teacher_id === currentUser.id || 
            cls.class_teacher_id === currentUser.id ||
            cls.assigned_teachers?.includes(currentUser.id)
          );
        }
        
        setClasses(teacherClasses);
      } else {
        setError('Failed to fetch classes');
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
      setError(error.message || 'Failed to fetch classes');
    } finally {
      setLoading(false);
    }
  };

  const handleClassClick = (cls) => {
    setSelectedClass(cls);
  };

  const handleViewClassInfo = () => {
    if (selectedClass) {
      onTabChange('class-info', { classId: selectedClass.id || selectedClass.class_id });
    }
  };

  const handleViewStudents = () => {
    if (selectedClass) {
      onTabChange('students', { classId: selectedClass.id || selectedClass.class_id });
    }
  };

  return (
    <div className="class-management-module">
      <div className="module-header">
        <button className="back-button" onClick={onBack}>← 返回</button>
        <h2>我的班級</h2>
        <button className="refresh-button" onClick={fetchTeacherClasses} title="重新載入">
          🔄
        </button>
      </div>
      
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>載入中...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <p>錯誤: {error}</p>
          <button onClick={fetchTeacherClasses} className="retry-button">
            重試
          </button>
        </div>
      ) : (
        <>
          <div className="classes-grid">
            {classes.length > 0 ? (
              classes.map((cls, index) => (
                <div 
                  key={cls.id || cls.class_id || index} 
                  className={`class-card ${selectedClass?.id === cls.id ? 'selected' : ''}`}
                  onClick={() => handleClassClick(cls)}
                >
                  <div className="class-card-header">
                    <h3>{cls.name || cls.class_name || `班級 ${index + 1}`}</h3>
                  </div>
                  <div className="class-card-body">
                    <p><strong>學生人數:</strong> {cls.student_count || cls.students?.length || 0}</p>
                    {cls.subjects && Array.isArray(cls.subjects) && (
                      <p><strong>科目:</strong> {cls.subjects.join(', ')}</p>
                    )}
                    {cls.form_level && (
                      <p><strong>年級:</strong> {cls.form_level}</p>
                    )}
                    {cls.class_teacher_name && (
                      <p><strong>班主任:</strong> {cls.class_teacher_name}</p>
                    )}
                  </div>
                  <div className="class-card-actions">
                    <button 
                      className="action-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClassClick(cls);
                        handleViewClassInfo();
                      }}
                    >
                      查看詳情
                    </button>
                    <button 
                      className="action-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClassClick(cls);
                        handleViewStudents();
                      }}
                    >
                      查看學生
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-classes">
                <p>暫無班級資料</p>
                <p className="hint">您尚未被分配到任何班級</p>
              </div>
            )}
          </div>
          
          {selectedClass && (
            <div className="class-detail-modal">
              <div className="modal-content">
                <div className="modal-header">
                  <h3>{selectedClass.name || selectedClass.class_name}</h3>
                  <button 
                    className="close-button"
                    onClick={() => setSelectedClass(null)}
                  >
                    ✕
                  </button>
                </div>
                <div className="modal-body">
                  <div className="detail-section">
                    <h4>班級資訊</h4>
                    <p><strong>班級名稱:</strong> {selectedClass.name || selectedClass.class_name}</p>
                    {selectedClass.class_id && (
                      <p><strong>班級ID:</strong> {selectedClass.class_id}</p>
                    )}
                    {selectedClass.form_level && (
                      <p><strong>年級:</strong> {selectedClass.form_level}</p>
                    )}
                    {selectedClass.student_count !== undefined && (
                      <p><strong>學生人數:</strong> {selectedClass.student_count}</p>
                    )}
                    {selectedClass.class_teacher_name && (
                      <p><strong>班主任:</strong> {selectedClass.class_teacher_name}</p>
                    )}
                  </div>
                  
                  {selectedClass.subjects && Array.isArray(selectedClass.subjects) && selectedClass.subjects.length > 0 && (
                    <div className="detail-section">
                      <h4>科目</h4>
                      <ul>
                        {selectedClass.subjects.map((subject, idx) => (
                          <li key={idx}>{subject}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  <div className="modal-actions">
                    <button 
                      className="primary-button"
                      onClick={() => {
                        handleViewClassInfo();
                        setSelectedClass(null);
                      }}
                    >
                      查看完整資訊
                    </button>
                    <button 
                      className="primary-button"
                      onClick={() => {
                        handleViewStudents();
                        setSelectedClass(null);
                      }}
                    >
                      管理學生
                    </button>
                    <button 
                      className="secondary-button"
                      onClick={() => setSelectedClass(null)}
                    >
                      關閉
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ClassManagementModule;

